import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import dayjs from "dayjs";

// Material-UI components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// Icons
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventNoteIcon from "@mui/icons-material/EventNote";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

// Material Dashboard 2 PRO components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Layout components
import DashboardLayout from "parts/LayoutContainers/DashboardLayout";
import DashboardNavbar from "parts/Navbars/DashboardNavbar";
import Footer from "parts/Footer";

// Context
import { useSessionContext } from "context/SessionContext";
import { LanguageContext } from "context";
import { useNotification } from "context/NotificationContext";

// API
import { 
  getDailyRecords, 
  saveDailyRecord, 
  deleteDailyRecord, 
  type DailyRecord 
} from "@shared/api/dailyRecords";

// Utils
import { truncateWithEllipsis } from "utils/stringUtils";

const MAX_PREVIEW_LENGTH = 80;

function PersonalStatusRecord() {
  const { t, language } = useContext(LanguageContext) as any;
  const { session } = useSessionContext();
  const { showNotification } = useNotification();

  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<DailyRecord[]>([]);

  // フォームのState
  const [date, setDate] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [mind, setMind] = useState<number>(3);
  const [technique, setTechnique] = useState<number>(3);
  const [body, setBody] = useState<number>(3);
  const [diary, setDiary] = useState("");
  const [memo, setMemo] = useState("");

  // 過去記録の表示月用のState
  const [viewMonth, setViewMonth] = useState(() => dayjs().startOf("month"));

  // 削除用・詳細表示用ダイアログのState
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [detailRecord, setDetailRecord] = useState<DailyRecord | null>(null);

  // 評価ラベル
  const RATING_OPTIONS = useMemo(() => [
    { label: language === "en" ? "Excellent" : "大変良い", value: 5 },
    { label: language === "en" ? "Good" : "良い", value: 4 },
    { label: language === "en" ? "Normal" : "普通", value: 3 },
    { label: language === "en" ? "A bit bad" : "少しダメ", value: 2 },
    { label: language === "en" ? "Bad" : "ダメ", value: 1 },
  ], [language]);

  // 記録の取得
  const fetchRecords = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      const startDate = viewMonth.format("YYYY-MM-DD");
      const endDate = viewMonth.endOf("month").format("YYYY-MM-DD");
      const data = await getDailyRecords(session.access_token, startDate, endDate);
      if (data?.records) {
        setRecords(data.records);
      }
    } catch (err) {
      console.error(err);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("Common.get-error"),
        },
        3000
      );
    }
  }, [session?.access_token, viewMonth, t, showNotification]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleSave = async () => {
    if (!session?.access_token || !date) return;
    setLoading(true);
    
    try {
      const newRecord: DailyRecord = { date, mind, technique, body, diary, memo };
      const res = await saveDailyRecord(session.access_token, newRecord);

      if (res.success && res.data) {
        showNotification(
          {
            icon: "check",
            title: t("Common.success"),
            message: t("Common.updated-success"),
          },
          3000
        );

        const savedMonth = dayjs(res.data.date).startOf("month");
        if (savedMonth.isSame(viewMonth, "month")) {
          setRecords(prev => {
            const exists = prev.find(r => r.date === res.data.date);
            if (exists) {
              return prev.map(r => r.date === res.data.date ? res.data : r);
            }
            return [res.data, ...prev].sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
          });
        } else {
          setViewMonth(savedMonth);
        }

        // フォームのリセット
        setDiary("");
        setMemo("");
        setMind(3);
        setTechnique(3);
        setBody(3);
      }
    } catch (err) {
      console.error(err);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("Common.updated-error"),
        },
        3000
      );
    } finally {
      setLoading(false);
    }
  };

  const executeDelete = async () => {
    if (!deleteTargetId || !session?.access_token) return;
    setLoading(true);
    try {
      const res = await deleteDailyRecord(session.access_token, deleteTargetId);
      if (res.success) {
        showNotification(
          {
            icon: "check",
            title: t("Common.success"),
            message: t("Common.delete-success"),
          },
          3000
        );
        setRecords(prev => prev.filter(r => r.id !== deleteTargetId));
      }
    } catch (err) {
      console.error(err);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("Common.delete-error"),
        },
        3000
      );
    } finally {
      setLoading(false);
      setDeleteTargetId(null);
    }
  };

  const getRatingLabel = (val: number) => RATING_OPTIONS.find(o => o.value === val)?.label || "";
  const getCategoryLabel = (key: string) => t(key).replace("の状態", "").replace("State of ", "");

  const renderRatingButtons = (title: string, currentValue: number, setValue: (val: number) => void) => (
    <Box mb={1.5}>
      <Typography variant="subtitle2" fontWeight="bold" mb={0.5} color="#333">
        {title}
      </Typography>
      <Box display="flex" gap={1} flexWrap="wrap" justifyContent="space-between">
        {RATING_OPTIONS.map((option) => {
          const isSelected = currentValue === option.value;
          return (
            <Box
              key={option.value}
              component="button"
              onClick={() => setValue(option.value)}
              sx={{
                flex: 1,
                minWidth: "18%",
                py: 0.8, 
                px: 0.5,
                borderRadius: 2, 
                border: "1px solid",
                borderColor: isSelected ? "#3b82f6" : "#CCC", 
                backgroundColor: isSelected ? "#3b82f6" : "#FFF",
                color: isSelected ? "#FFF" : "#333",
                fontWeight: "bold",
                fontSize: "0.75rem",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: isSelected ? "#2563eb" : "#f9fafb",
                }
              }}
            >
              {option.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={1}>
        <Typography variant="h5" mb={1}>
          {t("DailyRecords.title")}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Card sx={{ p: 2, border: "1px solid #E5E5E5", boxShadow: "none" }}>
              <Box mb={1.5}>
                <Typography variant="subtitle2" fontWeight="bold" mb={0.5} color="#333">
                  {t("DailyRecords.date-label")}
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  size="small"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#FFF",
                      height: "36px"
                    }
                  }}
                />
              </Box>

              {renderRatingButtons(t("DailyRecords.mind"), mind, setMind)}
              {renderRatingButtons(t("DailyRecords.technique"), technique, setTechnique)}
              {renderRatingButtons(t("DailyRecords.body"), body, setBody)}

              <Box mb={1.5}>
                <Typography variant="subtitle2" fontWeight="bold" mb={0.5} color="#333">
                  {t("DailyRecords.diary")}
                </Typography>
                <TextField
                  multiline
                  rows={5}
                  fullWidth
                  placeholder={t("DailyRecords.diary-ph")}
                  value={diary}
                  onChange={(e) => setDiary(e.target.value)}
                  inputProps={{ maxLength: 5000 }}
                  helperText={`${diary.length} / 5000`}
                  FormHelperTextProps={{ sx: { textAlign: "right", mt: 0 } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#FFF",
                    }
                  }}
                />
              </Box>

              <Box mb={1.5}>
                <Typography variant="subtitle2" fontWeight="bold" mb={0.5} color="#333">
                  {t("DailyRecords.memo")}
                </Typography>
                <TextField
                  multiline
                  rows={5}
                  fullWidth
                  placeholder={t("DailyRecords.memo-ph")}
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  inputProps={{ maxLength: 5000 }}
                  helperText={`${memo.length} / 5000`}
                  FormHelperTextProps={{ sx: { textAlign: "right", mt: 0 } }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#FFF",
                    }
                  }}
                />
              </Box>

              <Box
                component="button"
                onClick={handleSave}
                disabled={loading}
                sx={{
                  width: "100%",
                  py: 1.2,
                  mt: 0,
                  borderRadius: 2,
                  border: "none",
                  backgroundColor: loading ? "#9ca3af" : "#3b82f6",
                  color: "#FFF",
                  fontWeight: "bold",
                  fontSize: "0.9rem",
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: loading ? "#9ca3af" : "#2563eb",
                  }
                }}
              >
                {t("DailyRecords.save-btn")}
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card sx={{ p: 2, minHeight: "100%", border: "1px solid #E5E5E5", boxShadow: "none" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="h6" color="#333">{t("DailyRecords.history-title")}</Typography>
                <Box display="flex" alignItems="center">
                  <IconButton onClick={() => setViewMonth(prev => prev.subtract(1, "month"))} size="small">
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mx: 1, color: "#333" }}>
                    {viewMonth.format(language === "en" ? "MMM YYYY" : "YYYY年M月")}
                  </Typography>
                  <IconButton onClick={() => setViewMonth(prev => prev.add(1, "month"))} size="small">
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              </Box>

              {records.length === 0 ? (
                <Box py={5} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    {t("DailyRecords.no-records")}
                  </Typography>
                </Box>
              ) : (
                records.map((record, index) => (
                  <Box 
                    key={record.id || index} 
                    sx={{ 
                      borderBottom: "1px solid #eee", 
                      pb: 1.5, 
                      mb: 1.5,
                      "&:last-child": { borderBottom: "none", mb: 0, pb: 0 }
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Box display="flex" alignItems="center">
                        <EventNoteIcon fontSize="small" sx={{ mr: 1, color: "#3b82f6" }} />
                        <Typography variant="subtitle2" fontWeight="bold" color="#333">
                          {record.date}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton size="small" onClick={() => setDetailRecord(record)} sx={{ mr: 0.5 }}>
                          <DescriptionOutlinedIcon fontSize="small" sx={{ color: "#3b82f6" }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => setDeleteTargetId(record.id!)}>
                          <DeleteOutlineIcon fontSize="small" sx={{ color: "#ef4444" }} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Box display="flex" gap={2} mb={0.5} flexWrap="wrap">
                      <Typography variant="caption" fontWeight="bold" sx={{ color: "#3b82f6" }}>
                        {getCategoryLabel("DailyRecords.mind")}: {getRatingLabel(record.mind)}
                      </Typography>
                      <Typography variant="caption" fontWeight="bold" sx={{ color: "#3b82f6" }}>
                        {getCategoryLabel("DailyRecords.technique")}: {getRatingLabel(record.technique)}
                      </Typography>
                      <Typography variant="caption" fontWeight="bold" sx={{ color: "#3b82f6" }}>
                        {getCategoryLabel("DailyRecords.body")}: {getRatingLabel(record.body)}
                      </Typography>
                    </Box>

                    {record.diary && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", mb: 0.5 }}>
                        <strong>{t("DailyRecords.diary")}:</strong> {truncateWithEllipsis(record.diary, MAX_PREVIEW_LENGTH)}
                      </Typography>
                    )}
                    {record.memo && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        <strong>{t("DailyRecords.memo")}:</strong> {truncateWithEllipsis(record.memo, MAX_PREVIEW_LENGTH)}
                      </Typography>
                    )}
                  </Box>
                ))
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* 詳細表示ダイアログ（サンプルコードのスタイルを適用） */}
      <Dialog
        open={!!detailRecord}
        onClose={() => setDetailRecord(null)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            height: "500px",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle>{detailRecord?.date} {t("patner.detail") || "詳細"}</DialogTitle>
        <DialogContent
          dividers
          sx={{
            overflowY: "auto",
            flex: 1,
            p: 0,
          }}
        >
          <Card
            sx={{
              m: 2,
              p: 2,
              height: "100%",
              overflowY: "auto",
            }}
          >
            {/* 状態表示 */}
            <MDBox display="flex" gap={3} mb={3} flexWrap="wrap">
              <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#3b82f6" }}>
                {getCategoryLabel("DailyRecords.mind")}: {detailRecord && getRatingLabel(detailRecord.mind)}
              </MDTypography>
              <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#3b82f6" }}>
                {getCategoryLabel("DailyRecords.technique")}: {detailRecord && getRatingLabel(detailRecord.technique)}
              </MDTypography>
              <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#3b82f6" }}>
                {getCategoryLabel("DailyRecords.body")}: {detailRecord && getRatingLabel(detailRecord.body)}
              </MDTypography>
            </MDBox>

            {/* 日記の全文表示 */}
            {detailRecord?.diary && (
              <MDBox mb={3}>
                <MDTypography variant="subtitle2" fontWeight="bold" color="dark" mb={1}>
                  {t("DailyRecords.diary")}
                </MDTypography>
                <MDTypography 
                  variant="body2" 
                  color="dark"
                  sx={{ 
                    lineHeight: 1.8,
                    letterSpacing: '0.02em',
                    fontSize: '0.9rem',
                    color: '#000000',
                  }}
                >
                  {detailRecord.diary
                    .replace(/。/g, "。\n")
                    .split("\n")
                    .filter((line) => line.trim() !== "")
                    .map((line, index) => (
                      <span key={index} style={{ display: "block", marginBottom: "16px" }}>
                        {line}
                      </span>
                    ))}
                </MDTypography>
              </MDBox>
            )}

            {/* メモの全文表示 */}
            {detailRecord?.memo && (
              <MDBox mb={1}>
                <MDTypography variant="subtitle2" fontWeight="bold" color="dark" mb={1}>
                  {t("DailyRecords.memo")}
                </MDTypography>
                <MDTypography 
                  variant="body2" 
                  color="dark"
                  sx={{ 
                    lineHeight: 1.8,
                    letterSpacing: '0.02em',
                    fontSize: '0.9rem',
                    color: '#000000',
                  }}
                >
                  {detailRecord.memo
                    .replace(/。/g, "。\n")
                    .split("\n")
                    .filter((line) => line.trim() !== "")
                    .map((line, index) => (
                      <span key={index} style={{ display: "block", marginBottom: "16px" }}>
                        {line}
                      </span>
                    ))}
                </MDTypography>
              </MDBox>
            )}
          </Card>
        </DialogContent>
        <DialogActions>
          <MDButton color="info" variant="gradient" onClick={() => setDetailRecord(null)}>
            {t("PlanCard.close") || "閉じる"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={!!deleteTargetId} onClose={() => setDeleteTargetId(null)}>
        <DialogTitle>{t("DailyRecords.delete-confirm-title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("DailyRecords.delete-confirm-msg")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDeleteTargetId(null)} color="light">
            {t("Common.cancel")}
          </MDButton>
          <MDButton onClick={executeDelete} color="error" variant="gradient">
            {t("DailyRecords.delete")}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default PersonalStatusRecord;